package er.prateek.crossly.data.model

import kotlinx.serialization.Serializable

/**
 * The app-wide "played today" streak: any completed game in any mode (word crossword, daily,
 * math crossword, riddles) keeps it alive for that local calendar day.
 *
 * Distinct from [StreakInfo] (the older daily-puzzle-only streak, still used to mark the daily
 * as done): dates here are in the player's **local** time zone, because "did I play today" is a
 * question about the player's day, not about the shared UTC daily puzzle.
 *
 * [current] is kept as stored even after a missed day so a lost streak can be restored -- read
 * the effective value through [er.prateek.crossly.progression.StreakManager], never directly.
 */
@Serializable
data class PlayStreak(
    val current: Int = 0,
    val longest: Int = 0,
    /** ISO-8601 local date of the last day a game was completed (or covered by a restore). */
    val lastPlayedDate: String? = null,
    /** Recent days actually played, newest last, capped -- drives the week strip. */
    val playedDates: List<String> = emptyList(),
    /** Missed days that a restore filled in -- drawn as "frozen" in the week strip. */
    val restoredDates: List<String> = emptyList(),
    val restoresUsed: Int = 0,
    /** Rewarded ads already watched toward restoring the break identified by [restoreProgressFor]. */
    val restoreAdsWatched: Int = 0,
    /** The [lastPlayedDate] of the break [restoreAdsWatched] belongs to; a different break starts from zero. */
    val restoreProgressFor: String? = null,
    /** The break (by [lastPlayedDate]) the restore sheet already auto-opened for -- it opens once per break. */
    val restorePromptedFor: String? = null,
)
